import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDSecondaryActionIconButton,
  FDStack,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { useSnackbarContext } from 'Context/SnackbarContext';
import fr from 'facet-rest';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useRoadmapPaths } from 'Components/UI/guidedHouseholdReview/pathsContext';
import DefaultValueLever from './DefaultValueLever';
import LeverGroupLayout from './LeverGroupLayout';

const useStyles = buildUseStyles('LeverFieldLayout', (theme) => ({
  icon: {
    '& > button': { // the icon button
      padding: 0,
      height: theme.spacing(4), // make icon button spacing thinner
      justifyContent: 'flex-start',
    },
  },
  labelledLevers: {
    marginLeft: theme.spacing(5), // compensate for missing icon at left of fields
  },
}));

const leverTypeToFields = {
  RoadmapExplorerOngoingIncomeChangeLever: ['amount', 'who', 'incomeCategoryId', 'startType', 'startDate', 'startAtAge', 'endType', 'endDate', 'endAtAge'],
  RoadmapExplorerOneTimeIncomeChangeLever: ['amount', 'who', 'incomeCategoryId', 'startType', 'startDate', 'startAtAge'],
  RoadmapExplorerOngoingSpendingChangeLever: ['amount', 'startType', 'startDate', 'startAtAge', 'endType', 'endDate', 'endAtAge'],
  RoadmapExplorerRetirementDateLever: ['plannedRetirementDate'],
  RoadmapExplorerChangePrimaryResidenceLever: ['state', 'start'],
};

const getLeverLayout = (leverType) => {
  const fields = leverTypeToFields[leverType];
  if (fields) {
    return { layout: LeverGroupLayout, fields };
  }
  return { layout: DefaultValueLever };
};

function updateLever(setLoading, setSaving, resource, onLeverUpdate, refreshSelectedPathDataFromLevers, displayErrorMessage) {
  return async () => {
    if (!resource.isDirty()) {
      return;
    }

    setLoading(true);
    setSaving(true);
    try {
      const [updatedLevers] = await resource.submit();
      onLeverUpdate(updatedLevers);
      await refreshSelectedPathDataFromLevers();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to update lever');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };
}

function onClearLever(setLoading, setSaving, rel, onLeverUpdate, refreshSelectedPathDataFromLevers, displayErrorMessage) {
  return async () => {
    setLoading(true);
    setSaving(true);
    try {
      const [updatedLevers] = await rel.link('related').delete();
      onLeverUpdate(updatedLevers);
      await refreshSelectedPathDataFromLevers();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to clear lever');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };
}

const LeverFieldLayout = ({
  loading, setLoading, setSaving, onTogglePinOptimistic, onLeverUpdate, isPinnedOptimistically = false,
}) => {
  const {
    resource, saving,
  } = useMetaLayoutContext();
  const { refreshSelectedPathDataFromLevers } = useRoadmapPaths();
  const { displayErrorMessage } = useSnackbarContext();

  const classes = useStyles();

  const clearRel = resource.withRelationship(
    fr.relationships.delete.name,
    (rel) => rel,
    () => null,
  );

  // Local state mirrors resource.isPinned and updates on resource change
  const [pinned, setPinned] = useState(() => isPinnedOptimistically || resource.isTruthy('isPinned'));

  useEffect(() => {
    // When a different resource instance is provided, sync local state
    setPinned(isPinnedOptimistically || resource.isTruthy('isPinned'));
  }, [resource]);

  const showClearIcon = pinned || resource.isTruthy('isActive');

  const onBlurLever = updateLever(setLoading, setSaving, resource, onLeverUpdate, refreshSelectedPathDataFromLevers, displayErrorMessage);

  const toggleIsPinned = async () => {
    const wasPinned = resource.isTruthy('isPinned');
    const nextPinned = !wasPinned;

    // Optimistic UI
    onTogglePinOptimistic?.(resource, nextPinned);
    resource.attr('isPinned', nextPinned);
    setPinned(nextPinned);

    try {
      await resource.submit();
    } catch (err) {
      // Revert on failure
      onTogglePinOptimistic?.(resource, wasPinned);
      resource.attr('isPinned', wasPinned);
      setPinned(wasPinned);
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to toggle lever pin');
    }
  };

  const ActionButtons = () => (
    <div className={classes.icon}>
      {showClearIcon ? (
        <FDSecondaryActionIconButton
          icon={'close'}
          onClick={onClearLever(setLoading, setSaving, clearRel, onLeverUpdate, refreshSelectedPathDataFromLevers, displayErrorMessage)}
        />
      ) : (
        <FDSecondaryActionIconButton
          icon={'unpin'}
          onClick={toggleIsPinned}
        />)}
    </div>
  );

  const { layout: LeverLayout, fields } = getLeverLayout(resource.type());
  const leverLayout = <LeverLayout disabled={loading || saving} onBlur={onBlurLever} fields={fields} />;

  if (resource.attr('label')) {
    return (
      <FDStack direction={'column'} width={'100%'}>
        <FDStack direction="row" alignItems={'center'}>
          <ActionButtons />
          <MetaControlField attr={'label'} />
        </FDStack>
        <div className={classes.labelledLevers}>
          {leverLayout}
        </div>
      </FDStack>
    );
  }

  return (
    <FDStack direction={'row'} justifyContent={'space-between'}>
      <ActionButtons />
      {leverLayout}
    </FDStack>
  );
};

LeverFieldLayout.propTypes = {
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setSaving: PropTypes.func,
  onTogglePinOptimistic: PropTypes.func,
  onLeverUpdate: PropTypes.func,
  isPinnedOptimistically: PropTypes.bool,
};

export default LeverFieldLayout;
